/**
 * Main App Component
 * Handles authentication routing for the workspace + conversation architecture.
 */

import * as React from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { QueryClientProvider, useQuery } from "@tanstack/react-query";
import { SessionProvider } from "@/features/auth/components/SessionProvider";
import { useSession } from "@/features/auth/hooks/useSession";
import { AppProviders } from "./providers/AppProviders";
import { FeedbackProvider } from "./providers/FeedbackContext";
import { FeedbackModal } from "@/layout/FeedbackModal";
import { MagicLinkVerify } from "../views/MagicLinkVerify";
import { LoginV2 } from "../views/LoginV2";
import { PlanGate } from "../features/billing/components/PlanGate";
import {
  ConversationProvider,
  useConversation,
  getLastViewedConversation,
} from "./providers/ConversationContext";
import { useConversation as useConversationDetailQuery } from "@/features/conversations/hooks";
import MainLayout from "../layout/MainLayout";
import { WorkspaceSettings } from "../views/settings/WorkspaceSettings";
import { queryClient } from "../lib/query/client";
import { apiFetch } from "../lib/apiClient";
import { PAYWALL_DISMISSED_KEY } from "../lib/authStorage";
import { ErrorBoundary } from "../common/components/ErrorBoundary";
import { SessionErrorBanner } from "../layout/SessionErrorBanner";
import { ConnectionErrorScreen } from "../common/components/ConnectionErrorScreen";
import { FeedbackConversationBridge } from "./providers/FeedbackConversationBridge";
import { MaintenanceOverlay } from "../common/components/MaintenanceOverlay";
import { isMaintenanceModeActive } from "../lib/maintenance";
import { resolveWorkspaceOnboarding } from "@/features/auth/lib/workspace-onboarding";
import { isDriveOnlyDeployment } from "@/lib/drive-deployment";
import { XaviorPulseLoader } from "../common/components/XaviorPulseLoader";
import { WoodCanvas } from "../common/components/WoodCanvas";
import { WorkspaceNameSetupScreen } from "../common/components/WorkspaceNameSetupScreen";
import {
  shouldShowWorkspaceNameSetup,
  hasWorkspaceNameSkipFlag,
  setWorkspaceNameSkipFlag,
} from "./workspace-name-setup-gate";
import { WorkspaceSetupScreen } from "../common/components/WorkspaceSetupScreen";
import { listWorkspaceModules } from "../lib/api/endpoints/workspaces";
import { IntegrationCallback } from "../views/IntegrationCallback";
import { PortalApp } from "@/portal/PortalApp";
import { useGlobalAutoHideScrollbars } from "@/common/hooks";
import { NutritionConnect } from "@/views/NutritionConnect";
import { NativeInitializer } from "../layout/NativeInitializer";
import { DeepLinkHandler } from "../layout/DeepLinkHandler";
import { OpenInAppBanner } from "../common/components/OpenInAppBanner";
import { ToastProvider, useToast } from "./providers/ToastContext";
import {
  buildConversationPath,
  resolveWorkspaceId,
} from "@/lib/workspace-routing";
import { queryKeys } from "@/lib/query/keys";
import { useWorkspaceModuleQueries } from "@/layout/sidebar/hooks/useWorkspaceModuleQueries";
import { useModulePane } from "./providers/ModulePaneContext";
import { useIsCompactLayout } from "@/common/hooks/useIsCompactLayout";
import { resolveInitialModule } from "./initial-module";
import { fetchInitialPaneState } from "@/lib/conversation-pane-state";
import { prefetchModuleChunk } from "@/lib/prefetch-module";
import { useWorkspacePlanTier } from "@/features/billing/hooks/useWorkspacePlanTier";
import { isWorkspaceDefaultConversation } from "@/types/conversations";
import { initWebKeyboardDetection } from "@/lib/capacitor";

// Initialize mobile web keyboard detection early, before any components mount
initWebKeyboardDetection();

// Handle OAuth redirects from providers that only allow origin URLs (e.g., Whoop).
// These providers redirect to https://app.xavior.ai?code=XXX&state=YYY instead of
// the backend callback URL. Detect this and forward to the API for token exchange.
(function handleOAuthRedirect() {
  const params = new URLSearchParams(window.location.search);
  const code = params.get("code");
  const state = params.get("state");
  if (
    code &&
    state &&
    (window.location.pathname === "/" ||
      window.location.pathname === "/integrations/callback")
  ) {
    // Resolve API base URL using same logic as env.ts
    const explicit = (import.meta.env.VITE_API_BASE_URL as string) || "";
    const apiBase =
      explicit ||
      (import.meta.env.PROD ? "https://api.xavior.ai" : "") ||
      (window.location.hostname === "app.xavior.ai"
        ? "https://api.xavior.ai"
        : "");
    if (!apiBase) return; // No API URL resolved — don't redirect to relative path
    const callbackUrl = `${apiBase}/integrations/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`;
    window.location.replace(callbackUrl);
  }
})();

/** How long to wait for the container to become ready before showing the error screen. */
const CONTAINER_WARMUP_TIMEOUT_MS = 20_000;

/**
 * Conversation Route Sync - keeps URL params and in-memory conversation state aligned.
 */
function ConversationRouteSync({ children }: { children: React.ReactNode }) {
  const {
    workspaceId: workspaceIdParam,
    conversationId,
    view,
    moduleId,
  } = useParams<{
    workspaceId?: string;
    conversationId?: string;
    view?: string;
    moduleId?: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    activeConversation,
    setActiveConversation,
    allConversations,
    isLoading,
  } = useConversation();
  const { selectedContainer } = useSession();
  const workspaceId = React.useMemo(
    () => workspaceIdParam || resolveWorkspaceId(selectedContainer),
    [workspaceIdParam, selectedContainer?.id, selectedContainer?.ownerId],
  );

  // Track last synced values to prevent loops
  const lastSyncedConversationId = React.useRef<string | null>(null);
  // Track which direction triggered the sync to prevent ping-pong
  const syncSource = React.useRef<"url" | "state" | null>(null);

  // Unified sync effect - handles both URL→State and State→URL
  React.useEffect(() => {
    // Skip sync if loading or no conversations
    if (isLoading || allConversations.length === 0) return;

    // The settings route is intentionally conversation-less. Without this guard
    // the State→URL branch below immediately redirects /settings back to a
    // conversation URL (it has no :conversationId), so Workspace Settings could
    // never stay open.
    if (location.pathname === "/settings") return;

    const searchSuffix = location.search;
    const buildPathForConversation = (targetConversationId: string) => {
      if (moduleId) {
        return buildConversationPath(workspaceId, targetConversationId, {
          moduleId,
          search: searchSuffix,
        });
      }
      return buildConversationPath(workspaceId, targetConversationId, {
        view: view === "chat" ? "chat" : "workspace",
        search: searchSuffix,
      });
    };

    // URL → State sync: URL has conversation ID that doesn't match state
    if (conversationId && conversationId !== activeConversation?.id) {
      // Skip if we just synced this from state
      if (
        syncSource.current === "state" &&
        lastSyncedConversationId.current === conversationId
      ) {
        syncSource.current = null;
        return;
      }

      const targetConversation = allConversations.find(
        (item) => item.id === conversationId,
      );
      if (targetConversation) {
        syncSource.current = "url";
        lastSyncedConversationId.current = conversationId;
        setActiveConversation(targetConversation);
        return;
      } else {
        // Invalid conversation ID - redirect to first available conversation in current workspace.
        navigate(buildPathForConversation(allConversations[0].id), {
          replace: true,
        });
        return;
      }
    }

    // Architectural improvement: Only sync State→URL when there's NO conversationId in URL
    // This makes URL the single source of truth and prevents redirect loops
    // State→URL sync is now restricted to only the "no conversation selected" case
    if (!conversationId) {
      const selectedWorkspaceId =
        selectedContainer?.id ?? selectedContainer?.ownerId;
      const workspaceDefaultConversation = allConversations.find((item) =>
        isWorkspaceDefaultConversation(item),
      );
      const lastConversationId = selectedWorkspaceId
        ? getLastViewedConversation(selectedWorkspaceId)
        : null;
      const lastConversation = lastConversationId
        ? allConversations.find((item) => item.id === lastConversationId)
        : null;
      const targetConversation =
        lastConversation ??
        workspaceDefaultConversation ??
        activeConversation ??
        allConversations[0];

      if (targetConversation) {
        // #1873: URL is the canonical source for the active module. We only
        // reach this branch when the URL has no conversation at all (e.g.
        // hard refresh on `/` or first load), which means the URL has no
        // module either. Storage is permitted to fill in *only because the
        // URL is empty*. If the URL had `/mod/<id>`, this branch wouldn't
        // run and MainLayout's URL-matches-real-module check would persist
        // it (write URL → storage) rather than the inverse.
        //
        // We still seed `rememberedModuleId` from storage here so that the
        // first navigate emits the final URL (avoiding a visible flash
        // where MainLayout would have to replace() in a follow-up effect),
        // but the storage read is now scoped as a *fallback for empty
        // URL only* — it never overrides a URL value.
        const rememberedModuleId = (() => {
          try {
            return (
              localStorage.getItem(`lastModule:${targetConversation.id}`) ||
              sessionStorage.getItem(`lastModule:${targetConversation.id}`)
            );
          } catch {
            try {
              return sessionStorage.getItem(
                `lastModule:${targetConversation.id}`,
              );
            } catch {
              return null;
            }
          }
        })();

        syncSource.current = "state";
        lastSyncedConversationId.current = targetConversation.id;
        // URL had no moduleId — fall back to storage (which itself was
        // last written from a URL, per MainLayout's URL→storage write
        // path). If storage is empty too, drop to the no-module path and
        // let MainLayout pick the conversation's first module.
        const path = rememberedModuleId
          ? buildConversationPath(workspaceId, targetConversation.id, {
              moduleId: rememberedModuleId,
              search: searchSuffix,
            })
          : buildPathForConversation(targetConversation.id);
        navigate(path, { replace: true });
        return;
      }
    }
  }, [
    conversationId,
    moduleId,
    allConversations,
    isLoading,
    activeConversation?.id,
    setActiveConversation,
    navigate,
    view,
    workspaceId,
    activeConversation,
    location.search,
    location.pathname,
    selectedContainer?.id,
    selectedContainer?.ownerId,
  ]);

  return <>{children}</>;
}

/**
 * Holds the loader until the rendered state is fully ready — no intermediate
 * frames where the shell has painted but data hasn't arrived yet.
 *
 * Six gates, in order:
 *   1. Initial conversations-list fetch (no cache → show loader).
 *   2. URL not yet resolved (still at `/`, waiting for ConversationRouteSync
 *      to navigate to `/w/WS/c/<id>[/mod/<mod>]`).
 *   3. Conversation detail fetch not yet complete — the LIST response returns
 *      `modules: []`, the real modules come only from the per-conversation
 *      detail fetch. Without this gate, MainLayout paints once with no module
 *      pane, then again when modules arrive — the flash the user sees.
 *   4. `activeConversation` not yet synced to the URL's conversationId.
 *   5. Workspace module list + sidebar preferences (what the rail filters and
 *      orders from) not yet settled — without this the rail's first paint falls
 *      back to DEFAULT_VISIBILITY / DEFAULT_RAIL_ORDER, then re-filters/orders.
 *   6. The initial module pane not yet resolved + seeded into the pane authority
 *      (and activeConversation.modules) — without this the pane flashes Chat
 *      before the last-open module renders.
 *
 * Gates 4-6 are bypassed during the OAuth-callback window (oauthBypassActive):
 * the user already paid the OAuth round-trip, so we don't stack another shell
 * transition on top. Subscribing to the detail query here also kicks it off if
 * nothing else has — no dependency on `setActiveConversation` firing first.
 */
function AuthenticatedAppShell() {
  const { isLoading, allConversations, error, activeConversation, patchActiveModules } =
    useConversation();
  const { conversationId, moduleId, view } = useParams<{
    conversationId?: string;
    moduleId?: string;
    view?: string;
  }>();
  const loc = useLocation();
  const navigate = useNavigate();
  const isInitialLoading = isLoading && allConversations.length === 0 && !error;

  const detailQuery = useConversationDetailQuery(conversationId ?? null);

  // OAuth-callback bypass: the backend redirects to /w/WS/c/<id>?calendar_connected=true
  // after a Google scope grant. The user has already paid the OAuth round-trip
  // latency at Google, so we don't want Gate 4 to add another shell transition
  // on top of that. We capture the URL flag at first render and hold the
  // bypass for ~10s — long enough for activeConversation to sync without
  // forcing a full session-long bypass that would re-introduce the flicker
  // Gate 4 was added to prevent.
  const [oauthBypassActive, setOauthBypassActive] = React.useState(() => {
    try {
      return (
        new URLSearchParams(window.location.search).get(
          "calendar_connected",
        ) === "true"
      );
    } catch {
      return false;
    }
  });
  React.useEffect(() => {
    if (!oauthBypassActive) return;
    const t = setTimeout(() => setOauthBypassActive(false), 10000);
    return () => clearTimeout(t);
  }, [oauthBypassActive]);

  // ── Gate 5 inputs: the workspace-module queries the sidebar/rail filter and
  // order from. Subscribing here (same query keys the rail uses) warms the
  // React Query cache before MainLayout mounts, so the rail's first paint shows
  // the user's final enabled set + order instead of DEFAULT_VISIBILITY /
  // DEFAULT_RAIL_ORDER, then re-filtering/re-ordering. ──
  const { token, selectedContainer } = useSession();
  const workspaceId = resolveWorkspaceId(selectedContainer);
  const { modulesReady } = useWorkspaceModuleQueries(token, workspaceId);

  // ── Gate 6 inputs: resolve the initial module pane BEFORE first paint so the
  // shell shows the correct open module (or chat) immediately — no Chat→module
  // pop-in on login / hard refresh. Confined to the first authenticated render
  // (desktop, non-OAuth) so mid-session conversation switches are untouched. ──
  const isCompact = useIsCompactLayout();
  const { setPaneModuleId, setPaneVisible, getPaneState } = useModulePane();
  const [initialModuleResolved, setInitialModuleResolved] =
    React.useState(false);
  const initialResolveStartedRef = React.useRef(false);

  // The authoritative server "last-open pane" for this conversation (the
  // conversation panel's persisted chatPane state) — the same source ChatBox
  // restores from, read here so the pane authority is correct on first paint.
  const initialPaneQuery = useQuery({
    queryKey: queryKeys.conversations.initialPane(conversationId ?? ""),
    queryFn: () =>
      fetchInitialPaneState(token ?? null, workspaceId, conversationId as string),
    enabled: Boolean(
      token && workspaceId && conversationId && !isCompact && !oauthBypassActive,
    ),
    staleTime: 30_000,
  });

  // Bare effect (runs each render, no-op after the one-shot ref flips): it must
  // fire as soon as its prerequisites become ready, whichever render that is.
  React.useEffect(() => {
    // First authenticated render only: never re-gate or re-seed on mid-session
    // conversation switches (ChatBox's hydration owns those — ADR 028).
    if (initialResolveStartedRef.current) return;
    if (loc.pathname === "/settings") return;
    if (allConversations.length === 0 || !conversationId) return;
    // Prerequisites: detail (real modules) + activeConversation synced + the
    // rail's module data settled — i.e. exactly the data the first paint needs.
    if (!detailQuery.data || !activeConversation || activeConversation.id !== conversationId) return;
    if (!modulesReady) return;

    // Compact/mobile and the OAuth fast-path keep the existing behavior — no
    // pre-seed; release the gate immediately.
    if (isCompact || oauthBypassActive) {
      initialResolveStartedRef.current = true;
      setInitialModuleResolved(true);
      return;
    }

    // The server last-open pane query must have settled (data — possibly null —
    // or errored). On error we treat it as "no persisted pane" and fall through.
    const paneSettled =
      initialPaneQuery.data !== undefined || initialPaneQuery.isError;
    if (!paneSettled) return;

    initialResolveStartedRef.current = true;

    const modules = detailQuery.data.conversation?.modules ?? [];

    // The pane RENDERS from activeConversation.modules (ChatBox's
    // baseConversationModules), not the pane authority. On a refresh/login of
    // the already-active conversation, ConversationContext restores it from the
    // conversations LIST (which carries modules: []) and the route-sync
    // optimization skips the detail-fetching setActiveConversation — so those
    // modules would otherwise only arrive via ChatBox's async panel fetch (the
    // module-content pop-in). Seed them from the detail snapshot we already hold
    // so the seeded pane has its module to render on the first paint. The list
    // query's staleTime keeps this from being clobbered; ChatBox's panel-bound
    // modules take over as the steady-state source.
    if (modules.length > 0 && (activeConversation.modules?.length ?? 0) === 0) {
      patchActiveModules(conversationId, () => modules);
    }

    let rememberedModuleId: string | null = null;
    try {
      rememberedModuleId =
        localStorage.getItem(`lastModule:${conversationId}`) ||
        sessionStorage.getItem(`lastModule:${conversationId}`);
    } catch {
      rememberedModuleId = null;
    }
    const persistedPane =
      initialPaneQuery.data && !initialPaneQuery.isError ? initialPaneQuery.data : null;

    const resolution = resolveInitialModule({
      moduleIdParam: moduleId ?? null,
      view: view ?? null,
      modules,
      persistedPane,
      rememberedModuleId,
      isCompact,
    });

    // Seed the pane authority with BACKGROUND provenance (intent:false). There
    // is no user gesture during the loader hold, so this can never outrank a
    // click; ChatBox's mount hydration re-asserts the same value idempotently.
    const pane = getPaneState();
    if (resolution.moduleId !== pane.moduleId) {
      setPaneModuleId(resolution.moduleId, { intent: false });
    }
    if (resolution.open !== pane.visible) {
      setPaneVisible(resolution.open, { intent: false });
    }

    // Canonicalize the URL so MainLayout's URL guard + ChatBox's hydration both
    // settle on the SAME module (URL-wins) rather than re-navigating post-paint.
    let targetPath: string | null = null;
    if (resolution.open && resolution.moduleId) {
      if (moduleId !== resolution.moduleId) {
        targetPath = buildConversationPath(workspaceId, conversationId, {
          moduleId: resolution.moduleId,
          search: loc.search,
        });
      }
      // Warm the chosen module's lazy chunk so the first paint shows the module
      // itself, not a code-split fallback.
      const mod = modules.find((m) => m.id === resolution.moduleId);
      if (mod) prefetchModuleChunk(mod.type);
    } else if (view !== "chat") {
      // Explicit chat-only — prevents MainLayout's default-module auto-open.
      targetPath = buildConversationPath(workspaceId, conversationId, {
        view: "chat",
        search: loc.search,
      });
    }
    if (targetPath) navigate(targetPath, { replace: true });

    setInitialModuleResolved(true);
  });

  // Render the workspace settings route. This early return MUST come after all
  // hooks above — placing it before them changes the hook count between renders
  // (the shell re-renders in place across route changes, it doesn't remount),
  // which trips React's rules-of-hooks and crashes the shell, so /settings never
  // appears.
  if (loc.pathname === "/settings") {
    return <WorkspaceSettings />;
  }

  if (isInitialLoading) {
    return <XaviorPulseLoader />;
  }

  // Initial fetch failed with no cached data — show a retry screen rather than
  // dropping the user into MainLayout's empty-state spinner.
  if (error && allConversations.length === 0) {
    return (
      <ConnectionErrorScreen
        error={error}
        onRetry={() => window.location.reload()}
      />
    );
  }

  const hasConversations = allConversations.length > 0;

  // Gate 2: URL still at `/` — ConversationRouteSync's effect is queued.
  if (hasConversations && !conversationId) {
    return <XaviorPulseLoader />;
  }

  // Gate 3: URL has conversationId but the detail response (with real modules)
  // hasn't landed yet. Holding here guarantees MainLayout's first paint has
  // full module data — no "chat full-width → module appears" flash.
  if (
    hasConversations &&
    conversationId &&
    (detailQuery.isLoading || !detailQuery.data)
  ) {
    return <XaviorPulseLoader />;
  }

  // Gate 4: detail has loaded but the ConversationContext hasn't synced
  // `activeConversation` to the URL's conversationId yet. Without this,
  // MainLayout briefly renders its no-conversation fallback (with the
  // inline pulse loader) before the route-sync effect catches up — the
  // user perceives this as the loader handing off to a smaller loader,
  // then back to the loader, then to chat.
  // Bypassed during the OAuth-callback window (see oauthBypassActive above).
  if (
    !oauthBypassActive &&
    hasConversations &&
    conversationId &&
    (!activeConversation || activeConversation.id !== conversationId)
  ) {
    return <XaviorPulseLoader />;
  }

  // Gate 5: the sidebar rail filters + orders modules from the workspace module
  // list and sidebar preferences. They are owned by SidebarRail and would
  // otherwise fetch only after it mounts — so its first paint would fall back to
  // DEFAULT_VISIBILITY / DEFAULT_RAIL_ORDER and then visibly re-filter/re-order.
  // We subscribe above (same query keys) and hold until both settle, so the
  // rail's first paint is already the user's final enabled set + order.
  // `modulesReady` is true when there is no token/workspace, and releases on
  // query error (renders defaults) — so it can never deadlock the shell.
  // Bypassed during the OAuth-callback window like Gates 4 and 6, so a cold
  // cache on the OAuth redirect doesn't stack another loader transition on top
  // of the round-trip the user already paid.
  if (!oauthBypassActive && !modulesReady) {
    return <XaviorPulseLoader />;
  }

  // Gate 6: the initial module pane hasn't been resolved + seeded yet (the
  // effect above). Holding here guarantees MainLayout/ChatBox's first paint
  // reads the correct open module (or chat) from the pane authority — no
  // Chat→module pop-in on login / hard refresh. Self-releases on compact or
  // query error (see the effect), so it never deadlocks. Bypassed during the
  // OAuth-callback window for the same reason as Gate 4 (the user already paid
  // the OAuth round-trip — don't add another shell transition on top).
  if (
    !oauthBypassActive &&
    hasConversations &&
    conversationId &&
    !initialModuleResolved
  ) {
    return <XaviorPulseLoader />;
  }

  return <MainLayout />;
}

function AppContent() {
  const {
    token,
    profile,
    status,
    error,
    refresh,
    setToken,
    containerReady,
    pendingInvites,
    selectedContainer,
    containers,
    containersLoaded,
    sessionError,
  } = useSession();
  const workspacePlanTier = useWorkspacePlanTier();
  // App-wide canonical auto-hide scrollbars: one document-level capture listener
  // toggles `.is-scrolling` on whatever scrolled, so every `.xv-scroll` surface
  // (modules, modals/dropdowns via portals, login, portal sub-app) reveals the
  // thumb while scrolling and hides it on idle. Mounted at the app root so the
  // controller is always present wherever `.xv-scroll` is used.
  useGlobalAutoHideScrollbars();
  // One-time gate: a freshly-created team workspace is named "Personal", so
  // prompt the owner to name it (+ themselves) before the chat loads. Set true
  // once they save/skip so it doesn't re-show within the session.
  const [workspaceNameDismissed, setWorkspaceNameDismissed] =
    React.useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const paymentStatusRef = React.useRef<string | null>(null);

  // Workspace warmup stuck-state escape hatch (#2475).
  // If the container warmup doesn't resolve within 20 s, flip this flag so
  // the !containerReady branch renders ConnectionErrorScreen instead of an
  // infinite spinner.  Cleared immediately once containerReady flips to true.
  const [containerLoadingTimedOut, setContainerLoadingTimedOut] =
    React.useState(false);

  // Voluntary paywall dismissal — persisted in sessionStorage so navigations
  // within the same tab don't re-trigger the monetization/checkout gate after
  // the user clicks X. This is the MONETIZATION dismissal only; it must NOT gate
  // the team-join (pending-invite) consent gate — see teamJoinDismissed below.
  const [paywallDismissed, setPaywallDismissed] = React.useState(
    () =>
      typeof window !== "undefined" &&
      window.sessionStorage.getItem(PAYWALL_DISMISSED_KEY) === "1",
  );
  const handlePaywallDismiss = React.useCallback(() => {
    window.sessionStorage.setItem(PAYWALL_DISMISSED_KEY, "1");
    setPaywallDismissed(true);
  }, []);

  // Team-join (pending-invite) consent gate dismissal — kept in component state
  // only (NOT sessionStorage) so it never leaks to/from the monetization paywall
  // flag and always resets on auth change. An invitee must ALWAYS see the gate on
  // a fresh login regardless of any prior paywall dismissal; dismissing it still
  // routes per PlanGate's close logic (return-to-existing-or-logout).
  const [teamJoinDismissed, setTeamJoinDismissed] = React.useState(false);
  React.useEffect(() => {
    // Reset on USER-IDENTITY change (profile.id), NOT raw token: the proactive
    // refresh rotates the token string ~every 14 min, and keying this on `token`
    // would re-slam a dismissed gate full-screen on every refresh. profile.id is
    // stable across refreshes and only changes when a different user signs in.
    setTeamJoinDismissed(false);
  }, [profile?.id]);
  const handleTeamJoinDismiss = React.useCallback(() => {
    setTeamJoinDismissed(true);
  }, []);

  // First-time user workspace setup screen: shown while workspace bootstraps
  // for users who have not yet completed onboarding. Dismissed once modules
  // exist. Dismissal is persisted per-user in localStorage so a reload mid-
  // onboarding (!isOnboardingComplete) does not re-trigger the 5s splash.
  //
  // Must be declared before the containerLoadingTimedOut effect — that
  // effect's deps array reads setupScreenDismissed, and re-ordering this
  // declaration after the effect would cause a TDZ error at runtime.
  const [setupScreenDismissed, setSetupScreenDismissed] = React.useState(false);

  React.useEffect(() => {
    if (containerReady) {
      setContainerLoadingTimedOut(false);
      return;
    }
    // Gate 6a intercepts before containerReady is ever checked, so the 20 s
    // timer result is never rendered during onboarding.  Skip it to avoid a
    // spurious state update (and the associated extra render) while the user
    // is still on WorkspaceSetupScreen.
    if (
      resolveWorkspaceOnboarding(selectedContainer, profile).needsOnboarding &&
      !setupScreenDismissed
    )
      return;
    const id = window.setTimeout(
      () => setContainerLoadingTimedOut(true),
      CONTAINER_WARMUP_TIMEOUT_MS,
    );
    return () => window.clearTimeout(id);
  }, [containerReady, profile?.onboardingState, selectedContainer?.onboardingCompleted, selectedContainer?.role, setupScreenDismissed]);

  // Post-onboarding entry (W6): the PlanPicker free CTA / enterApp do a full
  // reload to land in the app; on first paint the gate chain can flash a blank
  // before session/plan/onboarding/container state settles. A one-shot
  // sessionStorage marker (set just before those reloads) holds a deterministic
  // loader across that window — generalizing the oauthBypassActive idea to the
  // plan/demo entry — then clears once the app is genuinely ready (or a safety
  // timeout fires so a stuck state still falls through to the normal gates).
  const [enteringApp, setEnteringApp] = React.useState(() => {
    try {
      return sessionStorage.getItem("xavior:entering-app") === "1";
    } catch {
      return false;
    }
  });
  const appEntryReady =
    status === "authenticated" &&
    Boolean(profile) &&
    containersLoaded &&
    containerReady;
  React.useEffect(() => {
    if (!enteringApp) return;
    const clear = () => {
      try {
        sessionStorage.removeItem("xavior:entering-app");
      } catch {
        /* ignore */
      }
      setEnteringApp(false);
    };
    if (appEntryReady) {
      clear();
      return;
    }
    const t = window.setTimeout(clear, 10000);
    return () => window.clearTimeout(t);
  }, [enteringApp, appEntryReady]);

  React.useEffect(() => {
    if (!profile?.id || typeof window === "undefined") return;
    const setupSeenKey = `xavior:setup-seen:${profile.id}`;
    if (window.localStorage.getItem(setupSeenKey) === "1") {
      setSetupScreenDismissed(true);
    }
  }, [profile?.id]);

  // Zero-workspace is a VALID, authenticated state (Teams S4 / PDR 006 D4): a
  // user with no usable workspace and no pending invite (e.g. a removed member,
  // or a fresh signup whose creation failed) lands on the standalone
  // zero-workspace plan-gate below and is NEVER logged out. The old 12s
  // zero-workspace force-logout timer was removed here — the session
  // ends only on an explicit log-out elsewhere or an auth failure, never on this
  // valid state. `containersLoaded` still distinguishes a CONFIRMED-empty fetch (show the
  // gate) from an in-flight/errored one (keep the loader / retry banner), so we
  // don't flash the gate while a slow cold-start fetch is still resolving.
  const confirmedZeroWorkspace =
    status === "authenticated" &&
    Boolean(profile) &&
    containersLoaded &&
    !sessionError &&
    (containers?.length ?? 0) === 0 &&
    (pendingInvites?.length ?? 0) === 0;

  const handleSetupReady = React.useCallback(() => {
    // Modules are provisioned — drop the splash locally and hand off to
    // MainLayout, where the agent-driven BOOTSTRAP flow runs in the
    // "Welcome" conversation and flips onboarding_completed itself via
    // PATCH /auth/profile (ChatBox.tsx onboarding.complete directive +
    // 6-turn safety net).
    if (profile?.id && typeof window !== "undefined") {
      const setupSeenKey = `xavior:setup-seen:${profile.id}`;
      try {
        window.localStorage.setItem(setupSeenKey, "1");
      } catch {
        /* quota / private mode */
      }
    }
    setSetupScreenDismissed(true);
  }, [profile?.id]);

  const handleSetupTimeout = React.useCallback(() => {
    // The WorkspaceSetupScreen handles its own timeout UI; nothing to do here.
  }, []);

  const checkWorkspaceReady = React.useCallback(async (): Promise<boolean> => {
    if (!token) return false;
    try {
      const workspaceId = selectedContainer?.id ?? selectedContainer?.ownerId;
      if (!workspaceId) return false;
      const modules = await listWorkspaceModules(token, workspaceId);
      return modules.length > 0;
    } catch {
      return false;
    }
  }, [token, selectedContainer]);

  const upgradeRequested = React.useMemo(() => {
    const params = new URLSearchParams(location.search);
    return params.get("upgrade") === "1";
  }, [location.search]);

  const shouldShowConnectionError = (err: Error | null) => {
    if (!err) return false;
    const message = err.message.toLowerCase();

    if (
      message.includes("401") ||
      message.includes("403") ||
      message.includes("unauthorized") ||
      message.includes("session invalid")
    ) {
      return false;
    }

    return (
      message.includes("failed to fetch") ||
      message.includes("network") ||
      message.includes("timeout") ||
      message.includes("timed out") ||
      message.includes("502") ||
      message.includes("503") ||
      message.includes("504") ||
      message.includes("server")
    );
  };

  // Handle payment success redirect
  const paymentSessionIdRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    if (paymentStatusRef.current === null) {
      const params = new URLSearchParams(window.location.search);
      const paymentStatus = params.get("payment");
      paymentStatusRef.current = paymentStatus;
      // Capture session_id (Stripe replaces {CHECKOUT_SESSION_ID} on redirect)
      // before replaceState wipes the query string. /payment/confirm uses
      // this to look up the actual price from the Stripe subscription.
      paymentSessionIdRef.current = params.get("session_id");
      if (paymentStatus) {
        window.history.replaceState({}, "", window.location.pathname);
      }
    }

    const paymentStatus = paymentStatusRef.current;
    if (!paymentStatus) {
      return;
    }

    if (paymentStatus === "success") {
      if (!token) {
        return;
      }

      void (async () => {
        try {
          const response = await apiFetch<{
            success: boolean;
            accessToken?: string;
            user?: { isPaidAccount: boolean };
          }>("payment/confirm", {
            method: "POST",
            authToken: token,
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(
              paymentSessionIdRef.current
                ? { sessionId: paymentSessionIdRef.current }
                : {},
            ),
          });
          // Update token with new JWT that has is_paid=true
          if (response.accessToken) {
            console.log(
              "[App] Payment confirmed - updating token with is_paid=true",
            );
            setToken(response.accessToken);
          }
        } catch (error) {
          console.error("Payment confirmation failed", error);
        } finally {
          paymentStatusRef.current = null;
          await refresh();
          queryClient.invalidateQueries({ queryKey: ["me", "quota"] });
        }
      })();
    } else {
      paymentStatusRef.current = null;
      void refresh();
    }
  }, [refresh, token, setToken]);

  // Handle OAuth callback params (calendar_connected, integration_connected, error)
  // This runs at the App level so toasts show regardless of which modal/view is open
  const { showToast } = useToast();
  const oauthHandledRef = React.useRef(false);
  const clearFlagTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  React.useEffect(
    () => () => {
      if (clearFlagTimerRef.current) clearTimeout(clearFlagTimerRef.current);
    },
    [],
  );
  React.useEffect(() => {
    if (oauthHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);

    const calendarConnected = params.get("calendar_connected") === "true";
    const integrationConnected = params.get("integration_connected") === "true";
    const oauthError = params.get("error");

    const oauthErrors = [
      "oauth_failed",
      "oauth_denied",
      "oauth_missing_email",
      "no_calendar",
      "temporarily_unavailable",
    ];
    const isOAuthError = oauthError && oauthErrors.includes(oauthError);

    if (!calendarConnected && !integrationConnected && !isOAuthError) return;
    oauthHandledRef.current = true;

    // Only clean error params from URL — success params (calendar_connected,
    // integration_connected) are left for IntegrationsModal to consume so it
    // can call checkCalendarStatus() / loadIntegrations() before cleaning up.
    if (isOAuthError) {
      const url = new URL(window.location.href);
      url.searchParams.delete("error");
      window.history.replaceState({}, "", url.toString());
    }

    // Extended 15s so later-mounting modules (Drive, Calendar behind lazy load)
    // can still pick up the flag. Tracked for cleanup on unmount.
    const scheduleClear = () =>
      setTimeout(
        () => sessionStorage.removeItem("email_just_connected"),
        15000,
      );
    if (calendarConnected || integrationConnected) {
      sessionStorage.setItem("email_just_connected", "true");
      // Connect is now a full-page redirect, so THIS return handler is the single
      // post-connect dispatch point (the popup that used to emit these is gone):
      //  • integration-oauth-connected → mounted Gmail/Calendar modules re-initialize
      //  • xavior:integration-connected → MainLayout's centralized refetch
      //    (email/gmail/integration query groups + objects/modules/google-accounts)
      window.dispatchEvent(new CustomEvent("integration-oauth-connected"));
      window.dispatchEvent(new CustomEvent("xavior:integration-connected"));
      clearFlagTimerRef.current = scheduleClear();
    } else if (isOAuthError) {
      const messages: Record<string, string> = {
        oauth_denied:
          "Google authorization was denied. Please try again and grant the required permissions.",
        oauth_missing_email:
          "Could not determine your email. Please try connecting again.",
        no_calendar: "No Google Calendar found for your account.",
        oauth_failed:
          "Something went wrong connecting your account. Please try again.",
        temporarily_unavailable:
          "Service is temporarily unavailable. Please try connecting again in a moment.",
      };
      showToast(messages[oauthError!] || messages.oauth_failed, "error");
    }
  }, [showToast]);

  // Post-auth landing (PDR 010). All auth paths redirect to the app root ("/")
  // after a verified identity, which flows through the main gate chain
  // (getMainContent → PlanGate → name → app), so PlanGate is the first-run front
  // door and is never bypassed. There is no /welcome route and no standalone
  // demo-or-connect landing page (LandingDecision, the #3756 login-flow rework,
  // was retired) — demo is now only the not-connected fallback inside in-app
  // onboarding (OnboardingBeats). See docs/product/pdr/010-first-run-login-and-onboarding-flow.md.

  // Determine what to show based on auth state
  const getMainContent = () => {
    // 1) Still checking auth (refreshing cookie or loading session) → show loading spinner
    //    CRITICAL: We must NOT redirect to /login while status === 'checking'
    if (status === "checking") {
      return <XaviorPulseLoader label="Restoring session…" />;
    }

    // 2) Unauthenticated with a connection error → show connection error screen
    if (
      status === "unauthenticated" &&
      error &&
      shouldShowConnectionError(error)
    ) {
      return (
        <ConnectionErrorScreen
          error={error}
          onRetry={() => {
            window.location.reload();
          }}
        />
      );
    }

    // 2) Definitively unauthenticated → redirect to login
    //    This can only happen AFTER the refresh cookie attempt has completed
    if (status === "unauthenticated") {
      return <Navigate to="/login" replace />;
    }

    // 3) We have a token but failed to load profile → show connection error screen
    if (!profile && error) {
      const handleClearAndRetry = () => {
        // Clear session storage to force refresh token flow
        if (typeof window !== "undefined") {
          sessionStorage.clear();
        }
        window.location.reload();
      };

      return (
        <ConnectionErrorScreen error={error} onRetry={handleClearAndRetry} />
      );
    }

    // 4) Token exists, no profile yet, and no error → treat as loading
    if (!profile) {
      return <XaviorPulseLoader />;
    }

    // 5) Paywall / PlanGate — runs BEFORE the workspace setup screen so a
    //    fresh signup picks a plan and accepts agreements first. Returning
    //    users with `agreementsAcceptedAt` set + an active plan fall through
    //    immediately. See #2703.
    // Skip paywall on localhost unless explicitly enabled via VITE_ENABLE_PAYWALL
    const isLocalhost =
      typeof window !== "undefined" && window.location.hostname === "localhost";
    const forcePaywall = import.meta.env.VITE_ENABLE_PAYWALL === "true";
    const paywallEnabled =
      forcePaywall ||
      (!isLocalhost &&
        (import.meta.env.PROD || import.meta.env.MODE === "production"));

    // Avoid a setup-screen flash for fresh signups: while the workspace plan
    // tier is still loading, every paywall guard's `!tierLoading` clause
    // short-circuits to false even when the user has no agreements. The old
    // ordering then fell through to WorkspaceSetupScreen for a few frames
    // before PlanGate took over (#2703 follow-up). Holding on the pulse
    // loader until the tier resolves keeps the gate ordering deterministic
    // for users who actually need the paywall — i.e. those who haven't
    // accepted agreements yet. Returning users with `agreementsAcceptedAt`
    // set don't need to wait, since the paywall won't fire for them.
    if (
      paywallEnabled &&
      !profile.agreementsAcceptedAt &&
      workspacePlanTier === null
    ) {
      return <XaviorPulseLoader />;
    }

    const trialStatus = profile.trialStatus ?? null;

    // If trial status is missing entirely (supervisor unreachable), show connection error
    // instead of falling through to paywall. Only check when paywall is enabled (skip on localhost).
    if (
      paywallEnabled &&
      !profile.isPaidAccount &&
      !trialStatus &&
      !upgradeRequested &&
      profile.agreementsAcceptedAt
    ) {
      return (
        <ConnectionErrorScreen
          error={
            new Error(
              "Unable to verify account status. The server may be temporarily unavailable.",
            )
          }
          onRetry={() => window.location.reload()}
        />
      );
    }

    // Free Personal plan users are first-class after #1755/#1756. They only
    // hit PlanGate for the welcome terms gate (handled by PlanGate itself in
    // 'free_terms' mode) or for an explicit upgrade request.
    // Treat null (loading) as non-free to avoid flashing upgrade gates.
    const isFreePlan = workspacePlanTier === "free";
    const tierLoading = workspacePlanTier === null;

    // Pending team invite always wins — render PlanGate so the team-join welcome
    // gate (#1776) can fire, regardless of plan tier or upgrade state.
    const hasPendingTeamInvite = (pendingInvites?.length ?? 0) > 0;

    // A non-owned container is a current team membership. PlanGate uses this
    // (plus pending invites) to identify a user with team history who must not
    // be force-ToS'd. Brand-new NON-invited free signups (no invites, no
    // memberships) keep force-ToS.
    const hasTeamMembership = (containers ?? []).some(
      (c) => c.isOwner === false,
    );

    // A usable workspace is any container the user owns or is a member of — i.e.
    // any entry in `containers` (a pending invite is NOT a container; membership
    // only exists after accepting). When the user has zero containers there is
    // nothing to land on, so closing a gate / having no pending invite must end
    // in logout rather than a stranded empty state (handled defensively below).
    const hasUsableWorkspace = (containers ?? []).length > 0;

    // Billing surfaces are owner-only: a member of a workspace doesn't manage its
    // subscription (the owner pays), so a member is never paywalled or upgrade-
    // prompted for it. Treat unknown ownership (null container during load) as owner.
    const isWorkspaceOwner = selectedContainer?.isOwner !== false;

    const shouldShowPaywall =
      !profile.isPaidAccount &&
      paywallEnabled &&
      isWorkspaceOwner &&
      !isFreePlan &&
      !tierLoading &&
      (upgradeRequested ||
        !profile.agreementsAcceptedAt ||
        trialStatus?.isExpired ||
        (!trialStatus?.isTrialActive && !trialStatus?.isGracePeriod));

    const freePlanShouldShowPaywall =
      isFreePlan &&
      !tierLoading &&
      paywallEnabled &&
      (upgradeRequested || !profile.agreementsAcceptedAt);

    // Paid (Pro) users who explicitly click an "Upgrade to Team" button land
    // here with ?upgrade=1. They are already isPaidAccount=true so the normal
    // shouldShowPaywall guard skips them — but they still need to see PlanPicker
    // so they can choose the Team plan and create a new checkout session.
    const paidUpgradeShouldShowPaywall =
      profile.isPaidAccount && paywallEnabled && upgradeRequested;

    // Pending team invite — render the team-join welcome (#1776) whenever there
    // is a pending invite, UNLESS the user has dismissed THIS gate this session.
    // The dismissal is required so the gate's X actually works: closing calls
    // `handleTeamJoinDismiss` (onDismiss), and without this `!teamJoinDismissed`
    // guard the branch would re-render the gate on the next frame — making X a
    // no-op and (worse) trapping the invitee, since membership only exists after
    // accepting. With the guard, a closed gate falls through to the normal app
    // on an EXISTING workspace when the user has one (PlanGate selects it on
    // close), or to the defensive logout below when the user has none.
    //
    // CRITICAL: this uses a SEPARATE, in-memory dismissal (teamJoinDismissed),
    // NOT the monetization `paywallDismissed` sessionStorage flag. Sharing that
    // flag let a checkout-paywall dismissal (or a stale flag from another account
    // in the same tab) silently suppress a real invitee's team-join gate, even
    // trapping a zero-workspace invitee on an infinite loader. teamJoinDismissed
    // resets on identity (token) change, so an invitee ALWAYS sees the gate on a
    // fresh login regardless of any prior paywall dismissal. NEVER auto-join on
    // dismiss — the invite stays pending until explicitly accepted/ignored.
    if (hasPendingTeamInvite && !teamJoinDismissed) {
      return (
        <PlanGate
          user={profile}
          authToken={token}
          trialStatus={trialStatus}
          pendingInvites={pendingInvites}
          hasTeamMembership={hasTeamMembership}
          onDismiss={handleTeamJoinDismiss}
        />
      );
    }

    // Standalone zero-workspace plan-gate (Teams S4 / PDR 006 D4). A logged-in
    // user with NO usable workspace and no pending invite (e.g. a removed member,
    // or a fresh signup whose creation failed) is NEVER logged out — they land
    // here, still authenticated, and can create a Free workspace (or wait for an
    // invite). Ordered AFTER the pending-invite gate (so an open invite still
    // shows the join gate) and BEFORE the paywall branches so the free_terms
    // clause never evaluates for them even though useWorkspacePlanTier degrades a
    // null selectedContainer to 'free'.
    //
    // Only render the gate once the empty state is CONFIRMED (`containersLoaded`,
    // no error). While the containers fetch is still in flight (a slow cold
    // start), `hasUsableWorkspace` is also false but we must hold a neutral
    // loader instead of flashing the gate.
    if (!hasUsableWorkspace) {
      if (confirmedZeroWorkspace) {
        return <PlanGate user={profile} authToken={token} zeroWorkspace />;
      }
      return <XaviorPulseLoader label="Loading your workspace…" />;
    }

    if (
      !paywallDismissed &&
      (shouldShowPaywall ||
        freePlanShouldShowPaywall ||
        paidUpgradeShouldShowPaywall)
    ) {
      return (
        <PlanGate
          user={profile}
          authToken={token}
          trialStatus={trialStatus}
          pendingInvites={pendingInvites}
          hasTeamMembership={hasTeamMembership}
          onDismiss={handlePaywallDismiss}
        />
      );
    }

    // 6a) First-time workspace setup screen — only after the user has gotten
    //     past the paywall (above). Shown regardless of containerReady because
    //     free-tier users have containerReady=true and would never see it if
    //     it were gated on !containerReady.
    if (
      // Drive-only deployment: skip the "Setting up your workspace…" splash
      // (part of the onboarding tour we suppress on drive). This gate reads the
      // container's onboarding flag, which the profile coercion doesn't touch.
      !isDriveOnlyDeployment() &&
      profile &&
      resolveWorkspaceOnboarding(selectedContainer, profile).needsOnboarding &&
      !setupScreenDismissed
    ) {
      return (
        <WorkspaceSetupScreen
          onReady={handleSetupReady}
          onTimeout={handleSetupTimeout}
          checkReady={checkWorkspaceReady}
        />
      );
    }

    // 6b) Container warmup gate for paid/trial users — actual provisioning is
    //     happening so the workspace-specific copy is accurate here. Returning
    //     users without a container (free tier) skip this gate entirely because
    //     `containerReady === true` for them (see SessionProvider). Issue #2410
    //     called out that this branch must not bleed into the generic
    //     "Loading your workspace…" splash that returning users saw on every
    //     refresh (#2516, #2568).
    //
    //     If warmup hasn't resolved within 20 s (#2475 stuck-state escape hatch),
    //     surface ConnectionErrorScreen so the user always has a retry action.
    if (!containerReady) {
      if (containerLoadingTimedOut) {
        return (
          <ConnectionErrorScreen
            error={
              new Error(
                "Workspace is taking longer than expected to start. The server may be temporarily unavailable.",
              )
            }
            onRetry={() => window.location.reload()}
          />
        );
      }
      return <XaviorPulseLoader label="Preparing your workspace…" />;
    }

    // Redirect legacy /onboarding route to home — onboarding is now conversational
    if (location.pathname === "/onboarding") {
      return <Navigate to="/" replace />;
    }

    // A freshly-created workspace is named "Personal" by default — prompt the
    // owner for their name + a real workspace name before the chat loads
    // (otherwise the welcome beat reads "Welcome to Personal"). Team-only in
    // #3949; generalized to every plan tier in #3981. One-time per workspace
    // (localStorage flag covers skip across reloads; a successful save renames
    // the workspace so the condition no longer matches). Skippable.
    //
    // The full gate rationale — including why onboardingCompleted is only ever
    // a WIDENING disjunct (#3949: owners have no membership row, so the
    // containers endpoint ORs in the user-level `users.onboarding_completed`,
    // which every payment path sets TRUE at payment time) and how legacy
    // "Personal" workspaces are scoped out via the workspace-creation
    // recency window — lives in workspace-name-setup-gate.ts.
    if (
      shouldShowWorkspaceNameSetup({
        type: selectedContainer?.type,
        isOwner: selectedContainer?.isOwner,
        containerName: selectedContainer?.containerName,
        memberSince: selectedContainer?.memberSince,
        onboardingCompleted: selectedContainer?.onboardingCompleted,
        dismissed: workspaceNameDismissed,
        localStorageSkipFlag: hasWorkspaceNameSkipFlag(selectedContainer?.id),
      })
    ) {
      return (
        <WorkspaceNameSetupScreen
          token={token}
          initialName={profile?.name ?? ""}
          isTeam={selectedContainer?.type === "team"}
          onDone={(skipped) => {
            if (skipped) setWorkspaceNameSkipFlag(selectedContainer?.id);
            setWorkspaceNameDismissed(true);
            if (!skipped) void refresh();
          }}
        />
      );
    }

    // Authenticated and paid (or dev mode) - show app with URL routing
    return (
      <AppProviders>
        <ConversationProvider>
          <NativeInitializer />
          <FeedbackConversationBridge />
          <ConversationRouteSync>
            <AuthenticatedAppShell />
          </ConversationRouteSync>
        </ConversationProvider>
      </AppProviders>
    );
  };

  // Hold the entry loader across the post-onboarding reload (W6) — see
  // enteringApp above. Until the app is ready this prevents the gate chain from
  // mounting the shell mid-race (the blank); once ready (or after the timeout)
  // enteringApp clears and the normal routes render.
  if (enteringApp && !appEntryReady) {
    return <XaviorPulseLoader label="Setting up your workspace…" />;
  }

  return (
    <>
      {/* The nordic/atelier desk — rendered only while authenticated so it stays
          stable behind the post-auth loading gates without bleeding onto the
          (translucent) login screen. Self-gates to those themes; null otherwise. */}
      {token && <WoodCanvas />}
      <Routes>
      {/* Portal routes — always accessible without user authentication */}
      <Route path="/portal/*" element={<PortalApp />} />

      {/* Nutrition connect link — client-facing, no user session required */}
      <Route path="/connect/nutrition" element={<NutritionConnect />} />

      {/* Magic link verification route - always accessible */}
      <Route path="/auth/verify" element={<MagicLinkVerify />} />

      {/* OAuth callback route - always accessible (redirected from provider) */}
      <Route path="/integrations/callback" element={<IntegrationCallback />} />

      {/* Login route */}
      <Route path="/login" element={<LoginV2 />} />

      {/* Post-auth landing / decision page (login-flow rework) — all three auth
          paths redirect here after a verified identity. Renders the demo-or-connect
          decision directly (bypasses the paywall gate chain); returning+real users
          are redirected straight into the app by the page itself. */}

      {/* Legacy onboarding route — redirects to home */}
      <Route path="/onboarding" element={getMainContent()} />

      {/* Workspace settings — admin console for owners/admins */}
      <Route path="/settings" element={getMainContent()} />

      {/* Canonical workspace/conversation routes - require authentication */}
      <Route
        path="/w/:workspaceId/c/:conversationId/mod/:moduleId"
        element={getMainContent()}
      />
      <Route
        path="/w/:workspaceId/c/:conversationId/:view?"
        element={getMainContent()}
      />

      {/* Root and catch-all - require authentication */}
      <Route path="/*" element={getMainContent()} />
    </Routes>
    </>
  );
}

export default function App() {
  // Check maintenance mode at the root level
  // This runs before any providers or routing
  if (isMaintenanceModeActive()) {
    return <MaintenanceOverlay />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <BrowserRouter>
          {/* DeepLinkHandler and OpenInAppBanner are outside SessionProvider
              so they work even when user is logged out */}
          <OpenInAppBanner />
          <SessionProvider>
            <DeepLinkHandler />
            <FeedbackProvider>
              <ErrorBoundary>
                <AppContent />
              </ErrorBoundary>
              <FeedbackModal />
              <SessionErrorBanner />
            </FeedbackProvider>
          </SessionProvider>
        </BrowserRouter>
      </ToastProvider>
    </QueryClientProvider>
  );
}
